import styles from "./styles.module.scss";
import UserSelector from "../UserSelector";
import React, { useContext, useState, useEffect, useMemo, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { fetchSelf } from "app/actions/user";
import { useUrlSearch } from "app/utils/hooks";
import { syncAthleteContact } from "app/utils/syncContact";
import UserItems from "../UserItems";
import Label from "../Label";
import Modal from "../Modal";
import InformationModal from "../InformationModal";
import ErrorX from "../../../../public/images/error-x.svg";
import {
  schedulingFetchGet,
  schedulingFetchPost,
} from "app/utils/request/scheduling/fetch";
import { RootState } from "typedefs";
import UserModel from "app/models/UserModel";
import { useTranslation, Trans } from "react-i18next";
import { toast } from "react-toastify";
import { RoleType, UserContext } from "app/contexts/UserContext";
import { StudentProfileModel } from "app/models/scheduling/StudentProfileModel";
import { StudentProgramRegistrationModel } from "app/models/scheduling/StudentProgramRegistrationModel";
import { StripePurchaseModel } from "app/models/scheduling/StripePurchaseModel";
import CheckBox from "app/components/inputs/CheckBox";
import UserAvatar from "../UserAvatar";
import {
  getAge,
  createFamilyMember,
  familyMembershipErrorMessage,
} from "app/routes/Account/FamilyEdit/utils";
import FamilyMemberModal from "app/routes/Account/FamilyEdit/_components/FamilyMemberModal";
import {
  FamilyMemberFormData,
  FamilyMemberSubmitResult,
} from "app/routes/Account/FamilyEdit/types";

// A registerable dependent of the current user (CN-1323). Shape is
// StudentProfilePresenter#to_simple_json + birthdate, camelized by the request
// layer — the `id` matches what the search returns, so it threads through the
// existing registration payload unchanged.
interface RegisterableFamilyMember {
  id: string;
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string | null;
  externalId: string;
  profilePicture?: string | null;
  birthdate?: string | null;
}

// Helper function to ensure items are in StudentProgramRegistrationModel format
const ensureRegistrationModel = (item: any): StudentProgramRegistrationModel => {
  // Already a StudentProgramRegistrationModel
  if (item instanceof StudentProgramRegistrationModel) {
    return item;
  }

  // Plain student profile or UserModel - wrap it
  const profile = item instanceof StudentProfileModel
    ? item
    : new StudentProfileModel({
        id: item.id,
        firstName: item.firstName,
        lastName: item.lastName,
        email: item.email,
        phone: item.phone,
        profilePicture: item.profilePicture || item.avatar || '',
        calendarSubscriptionUrl: item.calendarSubscriptionUrl,
      });

  return new StudentProgramRegistrationModel(
    {
      id: item.registrationId || 0,
      status: item.status || 'pending',
      createdAt: item.createdAt || new Date().toISOString(),
      canRemove: item.canRemove !== undefined ? item.canRemove : true,
      paymentMethod: item.paymentMethod || 'offline',
      expiresAt: item.expiresAt,
    },
    profile
  );
};

const RegisterStudentsPopup = ({
  programModel,
  showModal,
}) => {
  const currentStudent = useSelector(
    (state: RootState): UserModel => state.user.data
  );
  const user = useContext(UserContext);
  const isFacilityScheduler = user.role === RoleType.SCHEDULER;
  const isAlreadyRegistered =
    programModel.completedRegistrants.some(
      r => (r.studentProfile?.id || r.id) === currentStudent.id?.toString()
    ) ||
    programModel.waitlistedRegistrants.some(
      r => (r.studentProfile?.id || r.id) === currentStudent.id?.toString()
    );
  const initialInvitedStudents = !isFacilityScheduler && !isAlreadyRegistered
    ? [currentStudent]
    : [];

  const [invitedStudents, setInvitedStudents] = useState(initialInvitedStudents);
  const [canConfirm, setCanConfirm] = useState(false);
  const [errorInfo, setErrorInfo] = useState(null);
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();
  const [searchParams] = useUrlSearch();

  const programId = programModel.id;

  // The head's registerable dependents (synced facility students) for this
  // program. Powers the "Your Family" picker, shown regardless of
  // restrict_additional_people. Empty for non-heads, so the section is hidden.
  const [familyMembers, setFamilyMembers] = useState<RegisterableFamilyMember[]>([]);
  // CFTB-3289: lets the head add a dependent without leaving the popup.
  const [showAddModal, setShowAddModal] = useState(false);

  const loadFamilyMembers = useCallback(async (): Promise<RegisterableFamilyMember[]> => {
    try {
      const response = await schedulingFetchGet(
        `/programs/${programId}/registerable_family_members`,
        {}
      );
      const list = Array.isArray(response) ? response : [];
      setFamilyMembers(list);
      return list;
    } catch {
      // No registerable family members (or not entitled) — section stays hidden.
      return [];
    }
  }, [programId]);

  useEffect(() => {
    loadFamilyMembers();
  }, [loadFamilyMembers]);

  const isAtFullCapacity =
      !programModel.withWaitlist &&
      invitedStudents.length >= (programModel.spotsRemaining || 0);

  // Calculate client-side split for preview (backend makes final decision)
  // All students in invitedStudents are NEW (not yet registered)
  const { registrationCandidates, waitlistCandidates } = useMemo(() => {
    const spotsAvailable = programModel.spotsRemaining || 0;

    // Split students based on available spots
    const registered = invitedStudents.slice(0, spotsAvailable);
    const waitlisted = invitedStudents.slice(spotsAvailable);

    return {
      registrationCandidates: registered.map(ensureRegistrationModel),
      waitlistCandidates: programModel.withWaitlist
        ? waitlisted.map(ensureRegistrationModel)
        : []
    };
  }, [invitedStudents, programModel.spotsRemaining, programModel.withWaitlist]);

  // Enable confirm button when there are new students to register
  useEffect(() => {
    const hasStudents = invitedStudents.length > 0;
    const withinCapacity = programModel.withWaitlist || invitedStudents.length <= programModel.spotsRemaining;
    setCanConfirm(hasStudents && withinCapacity);
  }, [invitedStudents, programModel.withWaitlist, programModel.spotsRemaining]);

  const handleRemoveClick = (registration: StudentProgramRegistrationModel) => {
    // Remove from cart (local state only, no API call)
    const studentId = registration.studentProfile?.id || registration.id;
    setInvitedStudents((old) =>
      old?.filter((student) => {
        const optionStudentId = student.studentProfile?.id || student.id;
        return optionStudentId !== studentId;
      })
    );
  };

  const handleSetInvitedStudents = (newStudents) => {
    // If no waitlist, prevent adding more students than available spots
    if (!programModel.withWaitlist && newStudents.length > programModel.spotsRemaining) {
      return;
    }
    setInvitedStudents(newStudents);
  };

  const isFamilyMemberSelected = (member: RegisterableFamilyMember) =>
    invitedStudents.some((s) => (s.studentProfile?.id || s.id) === member.id);

  const toggleFamilyMember = (member: RegisterableFamilyMember) => {
    if (isFamilyMemberSelected(member)) {
      setInvitedStudents((old) =>
        old.filter((s) => (s.studentProfile?.id || s.id) !== member.id)
      );
    } else {
      // Routed through the capacity-aware setter (a no-op past capacity without
      // a waitlist, mirroring the search-add path).
      handleSetInvitedStudents([...invitedStudents, member]);
    }
  };

  // CFTB-3291/3290: surface the parent themselves in "Your Family" so they can
  // opt out of the registration (required for restricted programs, where the
  // parent registers only their children). Only the athlete can register
  // themselves, and only when not already registered.
  const canRegisterSelf = !isFacilityScheduler && !isAlreadyRegistered;
  const selfAvatar =
    typeof currentStudent.avatar === "string" ? currentStudent.avatar : null;

  const isSelfSelected = () =>
    invitedStudents.some((s) => (s.studentProfile?.id || s.id) === currentStudent.id);

  const toggleSelf = () => {
    if (isSelfSelected()) {
      setInvitedStudents((old) =>
        old.filter((s) => (s.studentProfile?.id || s.id) !== currentStudent.id)
      );
    } else {
      handleSetInvitedStudents([currentStudent, ...invitedStudents]);
    }
  };

  // CFTB-3289: create a dependent on the spot, refresh the roster, and
  // pre-select the new member (mirrors the booking participant picker).
  const handleCreateMember = async (
    data: FamilyMemberFormData
  ): Promise<FamilyMemberSubmitResult> => {
    try {
      const created = await createFamilyMember(data);
      const roster = await loadFamilyMembers();
      const added = roster.find((m) => m.externalId === created.userId);
      if (added) {
        handleSetInvitedStudents([...invitedStudents, added]);
      }
      setShowAddModal(false);
      return { ok: true };
    } catch (err) {
      return { ok: false, message: familyMembershipErrorMessage(err) ?? undefined };
    }
  };

  const loadStudents = async (inputValue: string, _currentOptions: [], additional: { page: number }) => {
    const page = additional?.page || 1;

    try {
      const response = await schedulingFetchGet(`/programs/${programId}/registerable_student_profiles`,
        { query: inputValue, per_page: 20, page }, { withHeaders: true }
      );

      const { students, headers } = response;
      const [current_page, total_pages] = [Number(headers.get('Current-Page')), Number(headers.get('Total-Pages'))];
      const next_page = current_page < total_pages ? current_page + 1 : null;

      // Filter out students already in invitedStudents (candidates not yet saved)
      const invitedStudentIds = new Set(
        invitedStudents.map(s => s.studentProfile?.id || s.id)
      );
      const filteredStudents = students?.filter(
        student => !invitedStudentIds.has(student.id)
      ) || [];

      return {
        options: filteredStudents,
        hasMore: current_page < total_pages,
        additional: {
          page: next_page,
        },
      };
    } catch (error) {
      console.error('Error fetching data:', error);
      return {
        options: [],
        hasMore: false,
        additional: {
          page: null,
        },
      };
    }

  };


  const onConfirm = async () => {
    setCanConfirm(false);

    try {
      if (invitedStudents.length > 0) {
        // Sync the current athlete into the scheduling service before the
        // purchase so they get a contact + space (mirrors the booking flow). A
        // failure blocks registration via the shared catch below. Facility
        // schedulers register other people, so they are excluded.
        if (!isFacilityScheduler) {
          await syncAthleteContact(
            currentStudent,
            searchParams.coach_id || programModel.coaches[0]?.externalId,
          );
          await (dispatch as any)(fetchSelf());
        }

        // Determine which students to send based on waitlist settings
        const studentsToRegister = programModel.withWaitlist
          ? invitedStudents // With waitlist: send all, backend decides split
          : invitedStudents.slice(0, programModel.spotsRemaining); // No waitlist: only send what fits

        const studentIds = studentsToRegister.map((item) => item?.id);

        const purchaseResponse = await schedulingFetchPost(`/programs/${programId}/stripe_purchases`, {
          registrations: studentIds.map(id => ({ student_profile_id: id }))
        });

        // Free clinics register students immediately — there is no purchase or checkout step.
        if (purchaseResponse?.free) {
          showModal(false);
          toast.success(
            purchaseResponse.waitlisted > 0
              ? t("Registration complete. Some athletes were added to the waitlist.")
              : t("You're registered")
          );
          history.go(0);
          return;
        }

        const purchase = new StripePurchaseModel(purchaseResponse);

        showModal(false);
        history.push({
          pathname: `/purchase/${purchase.id}`,
          state: {
            program: programModel,
            purchase,
          }
        });
      }
    } catch (error) {
      setCanConfirm(true);
      const apiMessage = Array.isArray(error?.errors)
        ? error.errors.map(e => e.message).join(', ')
        : error?.errors?.message;
      setErrorInfo({
        message: t("Registration failed"),
        details: apiMessage || "",
      });
    }
  };

  const confirmWithDisableOptionButton = (text, onClick) => (
    <button
      data-testid="save-edit"
      form="form-details"
      className={canConfirm ? styles.confirm_button : styles.disabled_button}
      type="submit"
      disabled={!canConfirm}
      onClick={onClick}
    >
      {text}
    </button>
  );

  const cancelButton = (text, onClick) => (
    <button
      data-testid="cancel-edit"
      className={styles.cancel_button}
      onClick={onClick}
    >
      {text}
    </button>
  );

  return (
    <>
      <Modal
        showModal={showModal}
        title={t("Registration Confirmation")}
        showCloseIcon={true}
        confirmButton={confirmWithDisableOptionButton(t("Confirm"), onConfirm)}
        cancelButtonProp={cancelButton(t("Cancel"), () => showModal(false))}
        width={"560px"}
        zIndex={3}
      >
        <div className={styles.register_container}>
          <div className={styles.column_container}>
            {/* Your Family — register managed children (CN-1323). Always shown
                when the head has registerable dependents, regardless of
                restrict_additional_people: your own child isn't an "additional
                participant". */}
            {familyMembers.length > 0 && (
              <>
                <div className={styles.section_header}>{t("Your Family")}</div>
                {/* The parent themselves (CFTB-3291) — toggleable so they can
                    register only their children on restricted programs (CFTB-3290). */}
                {canRegisterSelf && (
                  <CheckBox
                    className={styles.family_member_checkbox}
                    squareCheckIcons
                    checked={isSelfSelected()}
                    onChange={toggleSelf}
                    label={
                      <span className={styles.family_member_label}>
                        <UserAvatar
                          src={selfAvatar}
                          height={48}
                          width={48}
                          first_name={currentStudent.firstName}
                          last_name={currentStudent.lastName}
                        />
                        <span className={styles.family_member_text}>
                          <span className={styles.family_member_name}>
                            {currentStudent.firstName} {currentStudent.lastName}
                          </span>
                          <span className={styles.family_member_age}>
                            {t("You")}
                          </span>
                        </span>
                      </span>
                    }
                  />
                )}
                {familyMembers.map((member) => {
                  const age = member.birthdate ? getAge(member.birthdate) : null;
                  return (
                    <CheckBox
                      key={member.id}
                      className={styles.family_member_checkbox}
                      squareCheckIcons
                      checked={isFamilyMemberSelected(member)}
                      onChange={() => toggleFamilyMember(member)}
                      label={
                        <span className={styles.family_member_label}>
                          <UserAvatar
                            src={member.profilePicture}
                            height={48}
                            width={48}
                            first_name={member.firstName}
                            last_name={member.lastName}
                          />
                          <span className={styles.family_member_text}>
                            <span className={styles.family_member_name}>
                              {member.firstName} {member.lastName}
                            </span>
                            {age !== null && (
                              <span className={styles.family_member_age}>
                                {t("Age {{age}}", { age })}
                              </span>
                            )}
                          </span>
                        </span>
                      }
                    />
                  );
                })}
                {/* CFTB-3289: add a dependent without leaving registration. */}
                <button
                  type="button"
                  className={styles.add_member}
                  onClick={() => setShowAddModal(true)}
                >
                  {t("+ Add New Family Member")}
                </button>
              </>
            )}

            {/* Registration Candidates Section */}
            {registrationCandidates.length > 0 && (
              <>
                <div className={styles.section_header}>
                  {t("Registration Candidates")}
                </div>
                <UserItems
                  userList={registrationCandidates}
                  onClose={handleRemoveClick}
                  showEmail={isFacilityScheduler}
                  showPaymentMethod={false}
                  separatorStyle={styles.line_separator}
                  allowRemove={programModel.allowAdditionalPeople}
                  currentUserId={currentStudent.id?.toString()}
                />
              </>
            )}

            {/* Waitlist Candidates Section */}
            {waitlistCandidates.length > 0 && (
              <>
                <div className={styles.section_header}>
                  {t("Waitlist Candidates")}
                </div>
                <UserItems
                  userList={waitlistCandidates}
                  onClose={handleRemoveClick}
                  showEmail={isFacilityScheduler}
                  showPaymentMethod={false}
                  separatorStyle={styles.line_separator}
                  allowRemove={true}
                  currentUserId={currentStudent.id?.toString()}
                />
              </>
            )}

            {/* Add Additional Participants Section */}
            {!isAtFullCapacity && programModel.allowAdditionalPeople && (
              <Label
              text={t("Add Additional Participants")}
              item={(
                <UserSelector
                  key={invitedStudents.map(s => s.studentProfile?.id || s.id).join(',')}
                  showEmail={isFacilityScheduler}
                  placeholder={t("Search Athletes")}
                  values={loadStudents}
                  selectedUsers={invitedStudents}
                  setValues={handleSetInvitedStudents}
                  paginate={true}
                />
              )}
            /> )}

            {/* Capacity Message */}
            {!programModel.withWaitlist && (
              <div className={styles.payment_message}>
                {invitedStudents.length >= programModel.spotsRemaining
                  ? t("Program is at full capacity")
                  : t("{{remaining}} of {{total}} spots available", {
                    remaining: programModel.spotsRemaining - invitedStudents.length,
                    total: programModel.spotsRemaining,
                  })}
              </div>
            )}

            {/* Payment Hold Message — free clinics have no payment/hold step */}
            {invitedStudents.length > 0 && !programModel.isFree && (
              <div className={styles.payment_message}>
                <Trans i18nKey='After clicking "Confirm," your registrations will be held for 15 minutes while you complete the registration process.'>
                  After clicking &quot;Confirm,&quot; your registrations will be held <strong>for 15 minutes</strong> while you complete the registration process.
                </Trans>
              </div>
            )}
          </div>
        </div>
      </Modal>
      {errorInfo && (
        <InformationModal
          topImage={ErrorX}
          title={errorInfo.message}
          subtitle={errorInfo.details}
          handleAction={() => setErrorInfo(null)}
        />
      )}
      <FamilyMemberModal
        isOpen={showAddModal}
        mode="create"
        onClose={() => setShowAddModal(false)}
        onSubmit={handleCreateMember}
      />
    </>
  );
};

export default RegisterStudentsPopup;
